import { useEffect } from "react"
import { MoreVertical } from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Avatar,
  AvatarFallback,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from "@f2e/ui/components/base"
import { ellipsis } from "@f2e/shared/utils"

import { UserAvatar, UserNameplate } from "@/components"
import { Workspace } from "@/types"
import { useStore } from "@/store"
import { getUserFacingApiErrorMessage } from "@/lib/apiErrors"

import { ActionButton } from "./ActionButton"
import { useWorkspaceActions } from "../hooks"

const maxTitleLength = 54

export interface WorkspaceCardProps {
  workspace: Workspace
  onEdit?: (workspace: Workspace) => void
  onDelete?: (id: string) => void
  onLeave?: (id: string) => void
  onSetDefault?: (id: string) => void
  onMembers?: (workspace: Workspace) => void
}

const WorkspaceCard = ({
  workspace,
  onEdit,
  onDelete,
  onLeave,
  onMembers,
}: WorkspaceCardProps) => {
  const currentOrganization = useStore((state) => state.currentOrganization)
  const { t } = useTranslation()

  const {
    handleDeleteClick,
    handleLeaveClick,
    deleteError,
    deleteRespData,
    leaveError,
    leaveRespData,
    alertToast,
    renderAlerts,
  } = useWorkspaceActions({
    workspace,
    organizationId: currentOrganization!.id,
    onDeleteSuccess: onDelete,
    onLeaveSuccess: onLeave,
    showSuccessToast: true,
    navigateOnSuccess: false,
    maxNameLength: maxTitleLength,
  })

  useEffect(() => {
    if (!deleteError) return
    console.error("Failed to delete workspace", deleteError)

    alertToast({
      title: t("workspace.message.deleteFailed"),
      description: getUserFacingApiErrorMessage(
        deleteError,
        t,
        deleteRespData?.message || t("workspace.message.deleteFailed")
      ),
    })
  }, [deleteError, deleteRespData, alertToast, t])

  useEffect(() => {
    if (!leaveError) return
    console.error("Failed to leave workspace", leaveError)

    alertToast({
      title: t("workspace.message.leaveFailed"),
      description: getUserFacingApiErrorMessage(
        leaveError,
        t,
        leaveRespData?.message || t("workspace.message.leaveFailed")
      ),
    })
  }, [leaveError, leaveRespData, alertToast, t])

  const handleClick = () => {
    onEdit?.(workspace)
  }

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation()
    onEdit?.(workspace)
  }

  const handleMembers = (e: React.MouseEvent) => {
    e.stopPropagation()
    onMembers?.(workspace)
  }

  const displayTitle = (title: string) => {
    if (title.length > maxTitleLength) {
      return (
        <HoverCard>
          <HoverCardTrigger>{ellipsis(title, maxTitleLength)}</HoverCardTrigger>
          <HoverCardContent className="text-xs dark:text-gray-400 text-gray-500">
            {title}
          </HoverCardContent>
        </HoverCard>
      )
    }
    return <>{title}</>
  }

  return (
    <>
      <Card
        className="py-1 md:transition-all hover:cursor-pointer md:hover:scale-105 hover:bg-slate-50 dark:hover:bg-slate-900"
        onClick={handleClick}
      >
        <div className="flex w-full items-start gap-2">
          <CardHeader className="flex my-2 py-0 pr-0 flex-1 min-w-0">
            <CardTitle className="text-xl break-words">
              {displayTitle(workspace.name)}
            </CardTitle>
          </CardHeader>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="shrink-0 opacity-50 focus-visible:ring-0 focus-visible:ring-offset-0"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent onClick={(e) => e.stopPropagation()}>
              <ActionButton
                workspace={workspace}
                onEdit={handleEdit}
                onDelete={handleDeleteClick}
                onLeave={handleLeaveClick}
                onMembers={handleMembers}
              />
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <CardContent>
          <UserNameplate user={workspace.owner} size={12} />
        </CardContent>
        <CardFooter className="flex h-10 -space-x-2 pb-4">
          {workspace.members?.slice(0, 4).map((user) => (
            <UserAvatar key={user.id} user={user} />
          ))}
          {workspace.members?.length && workspace.members?.length > 4 && (
            <div className="pl-2">
              <HoverCard>
                <HoverCardTrigger asChild>
                  <Avatar className="h-6 w-6">
                    <AvatarFallback className="text-xs dark:text-gray-400 text-gray-500">
                      +{workspace.members?.length - 4}
                    </AvatarFallback>
                  </Avatar>
                </HoverCardTrigger>
                <HoverCardContent
                  className="w-auto"
                  side="bottom"
                  sideOffset={14}
                  align="start"
                >
                  <div className="flex flex-col text-sm">
                    {workspace.members?.slice(4, 10).map((user) => (
                      <div key={user.id}>{user.name}</div>
                    ))}
                    {workspace.members?.length &&
                      workspace.members?.length > 10 && <div>...</div>}
                  </div>
                </HoverCardContent>
              </HoverCard>
            </div>
          )}
        </CardFooter>
      </Card>

      {/* Confirmation Dialogs */}
      {renderAlerts()}
    </>
  )
}

export default WorkspaceCard
